import { Component, inject, signal, viewChild, OnInit, OnDestroy, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TabsModule } from 'primeng/tabs';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';

import {
  PartnerDashboardService,
  DashboardFilter,
  MemberRow,
  PaymentRow,
  currentFinYear,
} from '../../services/partner-dashboard';
import { PartnerService } from '../../services/partner';
import { AuthService } from '../../services/auth';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';
import { CoiDownloader } from '../../services/coi-downloader';
import { ReceiptDownloader } from '../../services/receipt-downloader';
import { PaymentService } from '../../services/payment';
import { IndDatePipe } from '../../pipes/ind-date';
import { PartnerFilterBar } from './partner-filter-bar';
import { CollectPaymentFlow, CollectTarget } from './collect-payment-flow';
import { CoiView } from '../policies/coi-view/coi-view';
import { SendCoiDialog } from '../policies/send-coi/send-coi-dialog';
import { ReceiptView } from '../payments/receipt/receipt-view';
import { PAY, KYC, Severity } from './status-severity';

/** Per-table state for a lazy-loaded p-table (page/size live in `first`/`pageSize`). */
class TableState<T> {
  rows = signal<T[]>([]);
  loading = signal(false);
  total = signal(0);
  first = signal(0); // paginator offset (0-based); page = first/pageSize + 1
  pageSize = signal(10);
  search = signal('');
  status = signal<string | null>(null); // paymentStatus filter (members + failed tables)
  kyc = signal<string | null>(null); // kycStatus filter (members table only)
}

@Component({
  selector: 'app-partner-records',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    TableModule,
    TagModule,
    ButtonModule,
    SelectModule,
    TabsModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    IndDatePipe,
    PartnerFilterBar,
    CollectPaymentFlow,
    DialogModule,
    TooltipModule,
    CoiView,
    SendCoiDialog,
    ReceiptView,
  ],
  templateUrl: './partner-records.html',
  styleUrls: ['./partner-records.css'],
})
export class PartnerRecords implements OnInit, OnDestroy {
  private svc = inject(PartnerDashboardService);
  private partnerSvc = inject(PartnerService);
  private auth = inject(AuthService);
  private toast = inject(ToastService);
  private cois = inject(CoiDownloader);
  private receipts = inject(ReceiptDownloader);
  private payment = inject(PaymentService);
  private platformId = inject(PLATFORM_ID);
  readonly pageTitle = inject(PageTitleService).title;

  // Shared certificate dialog (opened from a PAID member / successful payment row).
  showCoi = signal(false);
  coiMemberId = signal<number | null>(null);

  // Shared "Send Certificate" dialog — queues the COI over WhatsApp / SMS / Email.
  showSendCoi = signal(false);
  sendCoiMemberId = signal<number | null>(null);

  // Shared receipt dialog (opened from a successful payment row / PAID member).
  showReceipt = signal(false);
  receiptPaymentId = signal<number | null>(null);

  /** The inline collect-payment flow (opens Razorpay in place — no page redirect). */
  private flow = viewChild(CollectPaymentFlow);

  readonly isBrowser = isPlatformBrowser(this.platformId);
  readonly isAdmin = this.auth.isAdmin();

  /** Shared period + partner filter currently in effect (set by the filter bar). */
  private applied = signal<DashboardFilter>({ finYear: currentFinYear() });

  partnerOptions = signal<{ label: string; value: number }[]>([]);

  members = new TableState<MemberRow>();
  done = new TableState<PaymentRow>();
  failed = new TableState<PaymentRow>();

  activeTab = signal<string>('members');

  paymentStatusOptions = [
    { label: 'Unpaid', value: 'UNPAID' },
    { label: 'Paid', value: 'PAID' },
    { label: 'Refunded', value: 'REFUNDED' },
  ];
  kycStatusOptions = [
    { label: 'Verified', value: 'VERIFIED' },
    { label: 'Pending', value: 'PENDING' },
    { label: 'Not Verified', value: 'NOT_VERIFIED' },
  ];
  failedStatusOptions = [
    { label: 'Failed', value: 'FAILED' },
    { label: 'Cancelled', value: 'CANCELLED' },
    { label: 'Pending', value: 'PENDING' },
    { label: 'Initiated', value: 'INITIATED' },
  ];

  private timers: Record<string, any> = {};

  // ===================== lifecycle =====================
  ngOnInit() {
    if (!this.isBrowser) return;
    if (this.isAdmin) this.loadPartners();
    this.loadMembers();
    this.loadDone();
    this.loadFailed();
  }

  ngOnDestroy() {
    Object.values(this.timers).forEach((t) => clearTimeout(t));
  }

  /** Filter bar emitted a new period/partner selection — reset every table to page 1 and reload. */
  onFilter(f: DashboardFilter) {
    this.applied.set(f);
    this.members.first.set(0);
    this.done.first.set(0);
    this.failed.first.set(0);
    this.loadMembers();
    this.loadDone();
    this.loadFailed();
  }

  private baseFilter(): DashboardFilter {
    return { ...this.applied() };
  }

  private loadPartners() {
    this.partnerSvc.getAll().subscribe({
      next: (res: any) => {
        const data = res?.data ?? res ?? [];
        const list = Array.isArray(data) ? data : [];
        this.partnerOptions.set(
          list.map((p: any) => ({
            label: p.partnerName ?? p.partnerCode ?? `Partner #${p.id}`,
            value: p.id,
          })),
        );
      },
      error: () => {},
    });
  }

  // ===================== loaders =====================
  loadMembers() {
    if (!this.isBrowser) return;
    const t = this.members;
    const f: DashboardFilter = {
      ...this.baseFilter(),
      search: t.search().trim() || undefined,
      paymentStatus: t.status() || undefined,
      kycStatus: t.kyc() || undefined,
      page: Math.floor(t.first() / t.pageSize()) + 1,
      pageSize: t.pageSize(),
    };
    t.loading.set(true);
    this.svc.insuredMembers(f).subscribe({
      next: (res) => this.applyPaged(t, res),
      error: () => this.failPaged(t),
    });
  }

  loadDone() {
    if (!this.isBrowser) return;
    const t = this.done;
    const f: DashboardFilter = {
      ...this.baseFilter(),
      search: t.search().trim() || undefined,
      page: Math.floor(t.first() / t.pageSize()) + 1,
      pageSize: t.pageSize(),
    };
    t.loading.set(true);
    this.svc.successfulPayments(f).subscribe({
      next: (res) => this.applyPaged(t, res),
      error: () => this.failPaged(t),
    });
  }

  loadFailed() {
    if (!this.isBrowser) return;
    const t = this.failed;
    const f: DashboardFilter = {
      ...this.baseFilter(),
      search: t.search().trim() || undefined,
      paymentStatus: t.status() || undefined, // blank = all non-success states
      page: Math.floor(t.first() / t.pageSize()) + 1,
      pageSize: t.pageSize(),
    };
    t.loading.set(true);
    this.svc.failedPayments(f).subscribe({
      next: (res) => this.applyPaged(t, res),
      error: () => this.failPaged(t),
    });
  }

  private applyPaged<T>(t: TableState<T>, res: any) {
    t.loading.set(false);
    if (res?.success) {
      t.rows.set(res.data.items);
      t.total.set(res.data.totalCount);
    } else {
      t.rows.set([]);
      t.total.set(0);
      this.toast.error(res?.message || 'Failed to load data');
    }
  }

  private failPaged<T>(t: TableState<T>) {
    t.loading.set(false);
    t.rows.set([]);
    t.total.set(0);
  }

  // ===================== table events =====================
  onMembersLazy(e: any) {
    this.members.first.set(e.first ?? 0);
    this.members.pageSize.set(e.rows ?? this.members.pageSize());
    this.loadMembers();
  }
  onDoneLazy(e: any) {
    this.done.first.set(e.first ?? 0);
    this.done.pageSize.set(e.rows ?? this.done.pageSize());
    this.loadDone();
  }
  onFailedLazy(e: any) {
    this.failed.first.set(e.first ?? 0);
    this.failed.pageSize.set(e.rows ?? this.failed.pageSize());
    this.loadFailed();
  }

  /** Debounced per-table search — resets to page 1 then reloads only that table. */
  onSearch(key: 'members' | 'done' | 'failed', value: string) {
    const t = this.tableOf(key);
    t.search.set(value);
    if (this.timers[key]) clearTimeout(this.timers[key]);
    this.timers[key] = setTimeout(() => {
      t.first.set(0);
      this.reloadTable(key);
    }, 350);
  }

  /** A per-table dropdown filter changed — reset to page 1 and reload only that table. */
  onTableFilter(key: 'members' | 'failed') {
    this.tableOf(key).first.set(0);
    this.reloadTable(key);
  }

  private tableOf(key: 'members' | 'done' | 'failed') {
    return key === 'members' ? this.members : key === 'done' ? this.done : this.failed;
  }

  private reloadTable(key: 'members' | 'done' | 'failed') {
    if (key === 'members') this.loadMembers();
    else if (key === 'done') this.loadDone();
    else this.loadFailed();
  }

  /** Payment date for a transaction row — when paid, else when the attempt was created. */
  paidDate(r: any): string | null {
    return r?.paidAt || r?.createdDate || null;
  }

  /** Up-to-two-letter initials for the member avatar. */
  initials(name?: string | null): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return 'M';
    return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
  }

  /** True for a member payment status that still needs collecting. */
  needsPayment(s?: string | null): boolean {
    const v = (s || '').toUpperCase();
    return v === 'UNPAID' || v === 'FAILED' || v === 'CANCELLED' || v === 'PENDING' || v === 'INITIATED';
  }

  /** Collect payment inline — opens Razorpay right here (verified) or OTP-first (unverified). */
  collect(t: CollectTarget) {
    this.flow()?.start(t);
  }

  /** A payment was completed in the inline flow — refresh every table so the rows update. */
  onPaid() {
    this.loadMembers();
    this.loadDone();
    this.loadFailed();
  }

  /** True once a member is issued (paid) — the certificate exists. */
  isIssued(s?: string | null): boolean {
    return (s || '').toUpperCase() === 'PAID';
  }

  /** Open the shared certificate dialog for a member (issued members only). */
  viewCoi(memberId: number) {
    if (!memberId) return;
    this.coiMemberId.set(memberId);
    this.showCoi.set(true);
  }

  /** Open the "Send Certificate" dialog — queues the COI over WhatsApp / SMS / Email. */
  sendCoi(memberId: number) {
    if (!memberId) return;
    this.sendCoiMemberId.set(memberId);
    this.showSendCoi.set(true);
  }

  // ===================== receipt actions =====================
  // Payment Done rows carry the paymentId directly (r.id); the receipt is keyed by paymentId.
  viewReceipt(paymentId: number) {
    if (!paymentId) return;
    this.receiptPaymentId.set(paymentId);
    this.showReceipt.set(true);
  }
  downloadReceipt(paymentId: number, uid?: string | null) {
    if (paymentId) this.receipts.downloadPdf(paymentId, uid ?? undefined);
  }

  // Member rows have no paymentId — resolve the member's successful payment first, then act.
  viewReceiptForMember(memberId: number) {
    this.resolvePayment(memberId, (p) => this.viewReceipt(p.id));
  }
  downloadReceiptForMember(memberId: number) {
    this.resolvePayment(memberId, (p) => this.downloadReceipt(p.id, p.paymentUniqueId));
  }

  /** Find the member's SUCCESS payment, then run `then` with it (toasts if there isn't one). */
  private resolvePayment(memberId: number, then: (p: any) => void) {
    if (!memberId) return;
    this.payment.payGetByMember(memberId).subscribe({
      next: (res: any) => {
        const rows = Array.isArray(res?.data) ? res.data : [];
        const paid = rows.find((r: any) => (r.paymentStatus || '').toUpperCase() === 'SUCCESS');
        if (paid) then(paid);
        else this.toast.error('No successful payment found for this member.');
      },
      error: () => this.toast.error("Could not load the member's payments."),
    });
  }

  // ===================== display helpers =====================
  inr(n: number | null | undefined): string {
    return '₹' + Number(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }

  paySeverity(s?: string | null): Severity {
    return (PAY[(s || '').toUpperCase()] ?? 'secondary') as Severity;
  }
  kycSeverity(s?: string | null): Severity {
    return (KYC[(s || '').toUpperCase()] ?? 'secondary') as Severity;
  }
}
